import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { AlertTriangle, ShieldAlert, CheckCircle2, Landmark, Monitor, User, Clock, Wallet } from 'lucide-react';
import apiService from '../../services/apiServices';
import { formatCurrency } from '../../utility';

/**
 * ShiftAwareAccountPicker
 * ═══════════════════════════════════════════════════════
 * Replaces a plain account dropdown for any financial
 * operation that debits/credits a company account.
 *
 * When user selects an account, this component calls
 * GET /accounts/:id/shift-context to resolve whether
 * that account is a POS cash drawer with an active shift.
 *
 * Three states are rendered:
 *   A) Regular account      → no warning
 *   B) Active drawer        → shift-warning card (info)
 *   C) Unattended drawer    → authorization warning
 *
 * Props:
 *   accounts     []          — list of accounts from Redux
 *   value        string      — selected account _id
 *   onChange     fn(id)      — called when selection changes
 *   onContextResolved fn(ctx) — called with full shift context data
 *   amount       number      — the amount being transacted (for expected-cash preview)
 *   disabled     bool        — disable the picker
 *   filterType   string      — optional filter: "Cash" | "Bank" etc.
 *   label        string      — field label
 *   allowUnattendedDrawer bool — whether to allow unattended drawer (admin permission)
 */
const ShiftAwareAccountPicker = ({
  accounts = [],
  value = '',
  onChange,
  onContextResolved,
  amount = 0,
  disabled = false,
  filterType,
  label = 'Pay From Account',
  allowUnattendedDrawer = false,
  transactionDirection = 'out', // 'out' = debit | 'in' = credit
}) => {
  const [shiftContext, setShiftContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unattendedConfirmed, setUnattendedConfirmed] = useState(false);

  const filteredAccounts = filterType
    ? accounts.filter(a => a.account_type === filterType)
    : accounts;

  const resolveShiftContext = useCallback(async (accountId) => {
    if (!accountId) {
      setShiftContext(null);
      onContextResolved?.(null);
      return;
    }
    setLoading(true);
    setUnattendedConfirmed(false);
    try {
      const { data } = await apiService.get(`/accounts/${accountId}/shift-context`);
      setShiftContext(data);
      onContextResolved?.(data);
    } catch (err) {
      console.error('[ShiftAwareAccountPicker] Failed to resolve context:', err);
      setShiftContext(null);
      onContextResolved?.(null);
    } finally {
      setLoading(false);
    }
  }, [onContextResolved]);

  useEffect(() => {
    resolveShiftContext(value);
  }, [value, resolveShiftContext]);

  const handleChange = (e) => {
    onChange?.(e.target.value);
  };

  const selectedAccount = accounts.find(a => a._id === value);

  // ── Determine drawer state ──────────────────────────────────────
  const isDrawer = shiftContext?.is_cash_drawer;
  const hasActiveShift = shiftContext?.shift_context?.has_active_shift;
  const isUnattended = isDrawer && !hasActiveShift && shiftContext?.warning === 'UNATTENDED_DRAWER';

  // Expected cash after this transaction
  const expectedAfter = hasActiveShift
    ? (transactionDirection === 'out'
      ? (shiftContext.shift_context.expected_cash - Number(amount))
      : (shiftContext.shift_context.expected_cash + Number(amount)))
    : null;

  return (
    <div>
      <style>{`
        .saw-label {
          font-size: 11px;
          font-weight: 900;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 8px;
          display: block;
        }
        .saw-option-grid {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .saw-option {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 14px 18px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          transition: all 0.18s;
          user-select: none;
        }
        .saw-option:hover { border-color: #94a3b8; background: #fff; }
        .saw-option.selected {
          border-color: #6366f1;
          background: #f5f3ff;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
        }
        .saw-option.drawer { border-color: #d4d4d4; }
        .saw-option.drawer.selected { border-color: #6366f1; }
        .saw-option-left { display: flex; align-items: center; gap: 12px; }
        .saw-icon { padding: 8px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
        .saw-icon.cash { background: #f0fdf4; color: #16a34a; }
        .saw-icon.bank { background: #eff6ff; color: #2563eb; }
        .saw-icon.drawer-icon { background: #fef9c3; color: #b45309; }
        .saw-option-name { font-weight: 800; font-size: 0.87rem; color: #1e293b; }
        .saw-option-type { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.06em; }
        .saw-option-right { text-align: right; }
        .saw-balance { font-family: 'JetBrains Mono', monospace; font-weight: 900; font-size: 0.95rem; color: #0f172a; }
        .saw-type-badge { font-size: 0.6rem; font-weight: 900; padding: 3px 8px; border-radius: 20px; text-transform: uppercase; }
        .saw-type-badge.cash-badge { background: #dcfce7; color: #15803d; }
        .saw-type-badge.bank-badge { background: #dbeafe; color: #1d4ed8; }
        .saw-type-badge.drawer-badge { background: #fef9c3; color: #92400e; }

        /* Shift context cards */
        .saw-ctx-card {
          margin-top: 12px;
          border-radius: 14px;
          padding: 18px 20px;
          border: 1.5px solid;
          animation: saw-fadein 0.25s ease;
        }
        @keyframes saw-fadein { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .saw-ctx-card.active-shift { background: #fffbeb; border-color: #fbbf24; }
        .saw-ctx-card.unattended { background: #fef2f2; border-color: #fca5a5; }
        .saw-ctx-title { font-size: 0.7rem; font-weight: 900; text-transform: uppercase; letter-spacing: 0.09em; display: flex; align-items: center; gap-6px: ; gap: 6px; margin-bottom: 10px; }
        .saw-ctx-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px; }
        .saw-ctx-item .lbl { font-size: 0.62rem; font-weight: 800; text-transform: uppercase; color: #92400e; display: block; margin-bottom: 2px; }
        .saw-ctx-item.unattended .lbl { color: #b91c1c; }
        .saw-ctx-item .val { font-size: 0.82rem; font-weight: 800; color: #1e293b; }
        .saw-ctx-expected { background: #fff; border-radius: 10px; padding: 10px 14px; display: flex; justify-content: space-between; align-items: center; border: 1px solid #fde68a; margin-bottom: 8px; }
        .saw-ctx-expected.danger { border-color: #fca5a5; background: #fff5f5; }
        .saw-ctx-expected .exp-label { font-size: 0.68rem; font-weight: 800; text-transform: uppercase; color: #78350f; }
        .saw-ctx-expected.danger .exp-label { color: #991b1b; }
        .saw-ctx-expected .exp-val { font-family: 'JetBrains Mono', monospace; font-weight: 900; font-size: 1rem; color: #0f172a; }
        .saw-ctx-expected.danger .exp-val { color: #dc2626; }
        .saw-ctx-note { font-size: 0.72rem; color: #92400e; font-weight: 600; line-height: 1.4; }
        .saw-ctx-note.danger { color: #b91c1c; }
        .saw-unattended-actions { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
        .saw-btn-ghost { background: transparent; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 7px 14px; font-size: 0.75rem; font-weight: 700; cursor: pointer; color: #64748b; }
        .saw-btn-ghost:hover { background: #f8fafc; }
        .saw-btn-warn { background: #fee2e2; border: 1.5px solid #fca5a5; border-radius: 8px; padding: 7px 14px; font-size: 0.75rem; font-weight: 800; cursor: pointer; color: #b91c1c; }
        .saw-btn-warn:hover { background: #fecaca; }
        .saw-loader { display: flex; align-items: center; gap: 8px; font-size: 0.75rem; color: #94a3b8; margin-top: 8px; padding: 10px; }
      `}</style>

      <span className="saw-label">{label}</span>

      <div className="saw-option-grid">
        {filteredAccounts.map(acc => {
          const isSelected = acc._id === value;
          const isCash = acc.account_type === 'Cash';
          const isBank = acc.account_type === 'Bank' || acc.account_type === 'Current' || acc.account_type === 'Savings';
          const isDrawerAcc = acc.is_cash_drawer;

          return (
            <div
              key={acc._id}
              className={`saw-option ${isSelected ? 'selected' : ''} ${isDrawerAcc ? 'drawer' : ''}`}
              onClick={() => !disabled && onChange?.(acc._id)}
              style={{ opacity: disabled ? 0.6 : 1 }}
            >
              <div className="saw-option-left">
                <div className={`saw-icon ${isDrawerAcc ? 'drawer-icon' : isCash ? 'cash' : 'bank'}`}>
                  {isDrawerAcc ? <Monitor size={16} /> : isCash ? <Wallet size={16} /> : <Landmark size={16} />}
                </div>
                <div>
                  <div className="saw-option-name">{acc.account_name}</div>
                  <div className="saw-option-type">
                    {isDrawerAcc ? `Cash Drawer · ${acc.register_id || 'POS'}` : acc.account_type}
                  </div>
                </div>
              </div>
              <div className="saw-option-right">
                <div className="saw-balance">{formatCurrency(acc.balance)}</div>
                <span className={`saw-type-badge ${isDrawerAcc ? 'drawer-badge' : isCash ? 'cash-badge' : 'bank-badge'}`}>
                  {isDrawerAcc ? '🏧 Drawer' : isCash ? 'Cash' : 'Bank'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Loading state while resolving shift context */}
      {loading && (
        <div className="saw-loader">
          <Spinner size="sm" animation="border" variant="secondary" />
          Resolving shift context...
        </div>
      )}

      {/* ── STATE B: Active Cash Drawer with Live Shift ── */}
      {!loading && isDrawer && hasActiveShift && shiftContext.shift_context && (
        <div className="saw-ctx-card active-shift">
          <div className="saw-ctx-title" style={{ color: '#92400e' }}>
            <AlertTriangle size={14} />
            Active Shift Detected
          </div>
          <div className="saw-ctx-grid">
            <div className="saw-ctx-item">
              <span className="lbl">Register</span>
              <span className="val">{shiftContext.register_id || 'POS'}</span>
            </div>
            <div className="saw-ctx-item">
              <span className="lbl">Shift Ref</span>
              <span className="val">#{shiftContext.shift_context.shift_reference}</span>
            </div>
            <div className="saw-ctx-item">
              <span className="lbl" style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><User size={10} />Cashier</span>
              <span className="val">{shiftContext.shift_context.cashier}</span>
            </div>
            <div className="saw-ctx-item">
              <span className="lbl" style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><Clock size={10} />Since</span>
              <span className="val">{new Date(shiftContext.shift_context.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>

          {amount > 0 && (
            <div className={`saw-ctx-expected ${expectedAfter < 0 ? 'danger' : ''}`}>
              <div>
                <div className="exp-label">Expected Drawer Cash After This</div>
                <div className="exp-val">{formatCurrency(expectedAfter)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="exp-label">Current</div>
                <div style={{ fontWeight: 800, fontSize: '0.8rem', color: '#64748b' }}>{formatCurrency(shiftContext.shift_context.expected_cash)}</div>
              </div>
            </div>
          )}

          <div className="saw-ctx-note">
            ⓘ This {transactionDirection === 'out' ? 'payment' : 'receipt'} will appear in the cashier's X Report
            under <strong>Admin / External Cash Movements</strong> with your name and timestamp.
          </div>
        </div>
      )}

      {/* ── STATE C: Unattended Cash Drawer ── */}
      {!loading && isUnattended && !unattendedConfirmed && (
        <div className="saw-ctx-card unattended">
          <div className="saw-ctx-title" style={{ color: '#b91c1c' }}>
            <ShieldAlert size={14} />
            No Active Shift on This Drawer
          </div>
          <div className="saw-ctx-item unattended" style={{ marginBottom: '10px' }}>
            <span className="lbl">Register: {shiftContext.register_id || 'POS'} has no cashier on duty.</span>
            <span className="val" style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>
              This movement will be recorded as an Admin Cash Movement
              (unassigned to any cashier shift).
            </span>
          </div>
          <div className="saw-ctx-note danger">
            ⚠ Physical cash from this drawer will move with no cashier reconciliation.
            Ensure a shift is open, or explicitly authorize this as an admin movement.
          </div>
          {allowUnattendedDrawer && (
            <div className="saw-unattended-actions">
              <button className="saw-btn-ghost" onClick={() => onChange?.('')}>Use Another Account</button>
              <button className="saw-btn-warn" onClick={() => setUnattendedConfirmed(true)}>
                Record as Admin Cash Movement ▶
              </button>
            </div>
          )}
          {!allowUnattendedDrawer && (
            <div className="saw-unattended-actions">
              <button className="saw-btn-ghost" onClick={() => onChange?.('')}>Use Another Account</button>
              <span style={{ fontSize: '0.72rem', color: '#b91c1c', fontWeight: 600, alignSelf: 'center' }}>
                Admin authorization required to proceed.
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── STATE C confirmed ── */}
      {!loading && isUnattended && unattendedConfirmed && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, color: '#b45309', fontSize: '0.78rem', fontWeight: 700 }}>
          <CheckCircle2 size={14} />
          Authorized as Admin Cash Movement (no active shift)
        </div>
      )}
    </div>
  );
};

export default ShiftAwareAccountPicker;
